import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

export type ErrorBoundaryProps = {
  children: ReactNode;
  /** Rendered instead of the tree that threw. `reset` re-mounts it. */
  fallback: (error: Error, reset: () => void) => ReactNode;
};

type State = { error: Error | null };

/**
 * The last line of defence.
 *
 * Without one of these, a single render throw anywhere in the app leaves a
 * blank white screen — and this app holds the only copy of the student's
 * record. A blank screen after two months of logging reads as "my data is
 * gone", which is both untrue and the worst thing this product could ever
 * say to someone. The record is in IndexedDB, entirely untouched by whatever
 * a component did wrong, and the fallback's whole job is to say so.
 *
 * Still a class, because `getDerivedStateFromError` and `componentDidCatch`
 * have no hook equivalent. This is the one place in the app that needs one.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Logged, never sent. There is no crash reporter and adding one would
    // put a student's screen contents on someone else's server, which the
    // privacy position does not allow.
    console.error('[agrojatra] render failed', error, info.componentStack);
  }

  override render() {
    const { error } = this.state;
    if (error === null) return this.props.children;
    return this.props.fallback(error, () => this.setState({ error: null }));
  }
}
